// UI 아트 패스(2026-10-23, 대표님 "글자로만 되어 있으면 이탈률 높아져"): 문 앞 카드·마을 퀘스트 카드의 아이콘·엠블럼·패널 틀 그림.
// 원화: Codex CLI image_gen(tools/concept/jobs/ui-art-pass.json, 참조 concept/ui/ui-village-hud.webp) → 마젠타 배경 제거 WebP(tools/concept/ui-art-process.py).
// 받기에 실패하면 그림을 숨기고 글자·CSS 모양만 남긴다(기존 화면과 같다). 그림은 장식이며 같은 뜻의 글자를 지우지 않는다(접근성).
import type { RegionId } from '../shared/levels';
import { el } from './dom';
import { assetUrl } from '../pwa/assetMap';

export type UiIcon =
  | 'emblem-r1' | 'emblem-r2' | 'emblem-r3' | 'emblem-r4' | 'emblem-r5' | 'emblem-r6'
  | 'diff-normal' | 'diff-hard'
  | 'row-hazard' | 'row-boss' | 'row-reward' | 'row-record' | 'row-enemy' | 'row-recommend'
  | 'btn-auto' | 'town-1' | 'town-2' | 'town-3' | 'town-4' | 'town-5' | 'town-6'
  // HUD 정리(2026-10-27, PR #211): 둥근 HUD 단추 그림(tools/concept/jobs/hud-slim-icons.json)
  | 'btn-daily' | 'btn-hero-pick' | 'btn-cam-reset' | 'btn-community';
export type UiFrame = 'frame-card' | 'frame-quest' | 'frame-plate';

/** 44px 둥근 HUD 단추 안 그림 크기(그림 자체는 여백 12%를 두어 실제 모양은 ~28px) */
export const HUD_ICON_PX = 32;

export function uiIconUrl(name: UiIcon): string { return assetUrl('ui/icons/' + name + '.webp'); }
export function uiFrameUrl(name: UiFrame): string { return assetUrl('ui/frames/' + name + '.webp'); }
export function regionEmblem(id: RegionId): UiIcon { return ('emblem-' + id) as UiIcon; }
export function townCrest(no: number): UiIcon | null { return no >= 1 && no <= 6 ? (('town-' + no) as UiIcon) : null; }

/** 아이콘 <img>(장식, alt 없음). 실패하면 숨긴다 */
export function uiIcon(name: UiIcon, px: number, parent: HTMLElement, css = ''): HTMLImageElement {
  const im = el('img', 'flex:0 0 auto;display:block;width:' + px + 'px;height:' + px + 'px;object-fit:contain;pointer-events:none;user-select:none;-webkit-user-drag:none;' +
    'filter:drop-shadow(0 1px 2px rgba(0,0,0,.7));' + css, parent);
  im.alt = '';
  im.setAttribute('aria-hidden', 'true');
  im.draggable = false;
  im.decoding = 'async';
  im.addEventListener('error', () => { im.style.display = 'none'; });
  im.src = uiIconUrl(name);
  return im;
}
/** 이미 만든 아이콘 그림 바꾸기(같은 그림이면 그대로) */
export function setUiIcon(im: HTMLImageElement, name: UiIcon): void {
  const url = uiIconUrl(name);
  if (im.dataset.icon === name) return;
  im.dataset.icon = name;
  im.style.display = 'block';
  im.src = url;
}

/**
 * 9-slice 패널 틀(border-image). 틀 그림을 먼저 받아 본 뒤, 받았을 때만 테두리 두께(widthPx)와 border-image를 입히고 onApplied를 부른다.
 * 받지 못하면 아무것도 바꾸지 않아 기존 CSS 테두리·여백이 그대로 남는다(PR #177 리뷰 P2: 실패 시 두꺼운 빈 테두리가 남지 않게)
 */
export function applyFrame(target: HTMLElement, name: UiFrame, slicePct: number, widthPx: number, onApplied: () => void = () => {}): void {
  const url = uiFrameUrl(name);
  const probe = document.createElement('img');
  probe.addEventListener('load', () => {
    const s = target.style;
    s.borderStyle = 'solid';
    s.borderWidth = widthPx + 'px';
    s.borderImage = 'url("' + url + '") ' + slicePct + '% fill / ' + widthPx + 'px stretch';
    onApplied();
  }, { once: true });
  probe.src = url;
}

/**
 * 대체 모양(SVG 문자열) 위에 아이콘 그림을 겹친다: 그림을 받으면 대체 모양을 숨기고, 실패하면 대체 모양이 그대로 보인다.
 * cell은 position이 정해진 칸(relative로 바꾼다)
 */
export function iconOver(cell: HTMLElement, name: UiIcon, px: number, fallbackHtml: string, css = ''): HTMLImageElement {
  cell.style.position = 'relative';
  const fb = el('div', 'display:grid;place-items:center;', cell);
  fb.innerHTML = fallbackHtml;
  const im = uiIcon(name, px, cell, 'position:absolute;left:50%;top:50%;transform:translate(-50%,-50%);' + css);
  // 그림을 바꿀 때마다(setUiIcon) 받으면 대체 모양을 숨기고, 실패하면 다시 보인다(PR #177 리뷰 P2)
  im.addEventListener('load', () => { fb.style.display = 'none'; });
  im.addEventListener('error', () => { fb.style.display = 'grid'; });
  return im;
}
